import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { LOGO_URL } from "@/lib/branding";

function GoogleButton() {
  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" className="w-full rounded-full" onClick={loginGoogle} data-testid="google-login-btn">
      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      Continuar con Google
    </Button>
  );
}

function LoginForm() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      navigate(data.role ? "/dashboard" : "/completar-perfil");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label>Correo electrónico</Label>
        <Input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="login-email-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Contraseña</Label>
        <Input
          type="password"
          required
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="login-password-input"
        />
      </div>
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
      </Button>
      <div className="text-center pt-1">
        <Link to="/recuperar-contrasena" className="text-sm text-muted-foreground hover:text-primary" data-testid="forgot-password-link">
          ¿Olvidaste tu contraseña?
        </Link>
      </div>
    </form>
  );
}

function RoleSelector({ role, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        onClick={() => onChange("patient")}
        data-testid="register-role-patient"
        className={`rounded-xl border p-3 text-sm font-semibold transition-colors duration-200 ${
          role === "patient" ? "border-primary bg-accent" : "hover:bg-secondary"
        }`}
      >
        Soy paciente
      </button>
      <button
        type="button"
        onClick={() => onChange("professional")}
        data-testid="register-role-professional"
        className={`rounded-xl border p-3 text-sm font-semibold transition-colors duration-200 ${
          role === "professional" ? "border-primary bg-accent" : "hover:bg-secondary"
        }`}
      >
        Soy profesional
      </button>
    </div>
  );
}

function ProfessionalFields({ form, setForm }) {
  return (
    <div className="rounded-xl bg-secondary p-4 space-y-3">
      <p className="text-xs text-muted-foreground">
        Tu cuenta será revisada por un administrador antes de activarse.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Título</Label>
          <Input
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            placeholder="Dra. / Dr."
            data-testid="register-titulo-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Cédula</Label>
          <Input
            value={form.cedula}
            onChange={(e) => setForm({ ...form, cedula: e.target.value })}
            data-testid="register-cedula-input"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Especialidad</Label>
        <Input
          value={form.especialidad}
          onChange={(e) => setForm({ ...form, especialidad: e.target.value })}
          placeholder="Esp. Salud Trans"
          data-testid="register-especialidad-input"
        />
      </div>
    </div>
  );
}

function RegisterForm({ initialRole }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: initialRole,
    titulo: "Dr.",
    especialidad: "",
    cedula: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { name: form.name, email: form.email, password: form.password, role: form.role };
      if (form.role === "professional") {
        body.professional_profile = {
          titulo: form.titulo,
          especialidad: form.especialidad,
          cedula: form.cedula,
          universidad: "",
          telefono: "",
          direccion: "",
        };
      }
      const { data } = await api.post("/auth/register", body);
      setUser(data);
      toast.success("Cuenta creada con éxito");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <RoleSelector role={form.role} onChange={(role) => setForm({ ...form, role })} />
      <div className="space-y-1.5">
        <Label>Nombre completo</Label>
        <Input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="register-name-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Correo electrónico</Label>
        <Input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="register-email-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Contraseña</Label>
        <Input
          type="password"
          required
          minLength={6}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="register-password-input"
        />
      </div>
      {form.role === "professional" && <ProfessionalFields form={form} setForm={setForm} />}
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="register-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear cuenta"}
      </Button>
    </form>
  );
}

export default function AuthPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("registro") ? "registro" : "login");
  const initialRole = params.get("rol") === "professional" ? "professional" : "patient";

  if (user) return <Navigate to={user.role ? "/dashboard" : "/completar-perfil"} replace />;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      <Link to="/" className="flex items-center gap-2 mb-8" data-testid="auth-logo-link">
        <img src={LOGO_URL} alt="TransSalud" className="h-10 w-10 object-contain" />
        <span className="font-heading font-semibold text-xl">
          TransSalud <span className="text-primary">· THAG</span>
        </span>
      </Link>

      <div className="w-full max-w-md bg-white border rounded-2xl p-6 sm:p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full rounded-full mb-6">
            <TabsTrigger value="login" className="rounded-full" data-testid="auth-tab-login">
              Iniciar sesión
            </TabsTrigger>
            <TabsTrigger value="registro" className="rounded-full" data-testid="auth-tab-register">
              Crear cuenta
            </TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <LoginForm />
          </TabsContent>
          <TabsContent value="registro">
            <RegisterForm initialRole={initialRole} />
          </TabsContent>
        </Tabs>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-widest">o</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <GoogleButton />
      </div>
    </div>
  );
}
